import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, UploadFileOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"
import type { UseQueryExecute } from "urql"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { SignedUrl } from "@/graphql/codegen/graphql"
import { OrderType } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { NotificationType } from "@/schemas/notification"
import { PurchaseOrderFileDialog } from "@/screens/Procurement/PurchaseOrder/components/dialogs/PurchaseOrderFileDialog"
import { PurchaseOrderFileTable } from "@/screens/Procurement/PurchaseOrder/components/PurchaseOrderFiles/tables/PurchaseOrderFileTable"
import { usePOFileUploadAction } from "@/screens/Procurement/PurchaseOrder/hooks/useHeaderActions/usePOFileUploadAction"
import usePurchaseOrderForm from "@/screens/Procurement/PurchaseOrder/hooks/usePurchaseOrderForm"
import { usePurchaseOrderReadonly } from "@/screens/Procurement/PurchaseOrder/hooks/usePurchaseOrderReadonly"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface PurchaseOrderFilesContainerProps {
  refetchFileUploadData: UseQueryExecute
  fileList: SignedUrl[]
  fileFetching: boolean
}

export const PurchaseOrderFilesContainer: FC<PurchaseOrderFilesContainerProps> = ({
  fileList,
  fileFetching,
  refetchFileUploadData,
}) => {
  const { purchaseOrderId } = useParams({ from: "/app/procurement/_layout_/purchase-orders/$purchaseOrderId" })
  const [expanded, setExpanded] = useState(true)

  const { dialogOpen, setDialogOpen, canUpload } = usePOFileUploadAction()

  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { isReadonly } = usePurchaseOrderReadonly()
  const { getField } = usePurchaseOrderForm()
  const orderType = getField("type")

  const { removeSingleFile } = useFilesRemove(refetchFileUploadData)

  const handleFileRemove = async (fileId: string) => {
    const onRemoveResult = await removeSingleFile(fileId)
    if (onRemoveResult) {
      notify({
        message: "Purchase Order file was removed successfully",
        type: NotificationType.Success,
      })
    } else {
      notify({
        message: "Purchase Order file removing was failed",
        type: NotificationType.Error,
      })
    }
  }

  const handleSuccess = () => {
    refetchFileUploadData()
    setDialogOpen(false)
  }

  // Hide the entire section if the user does not have permission and no files
  if (!canUpload && (!fileList || fileList.length === 0) && !fileFetching) {
    return null
  }

  return (
    <>
      {dialogOpen && (
        <PurchaseOrderFileDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={handleSuccess}
          orderId={purchaseOrderId}
          isQuote={orderType === OrderType.Quote}
        />
      )}
      <Paper className='border border-gray-300 p-6' elevation={0} data-testid='purchase-order-files-container'>
        <Box className='flex items-center justify-between'>
          <Typography variant='h6' data-testid='purchase-order-title'>
            Uploaded Files
          </Typography>
          <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label='show more'>
            {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
        <Collapse in={expanded} className='mt-6'>
          {fileList?.length ? (
            <Box className='flex flex-col'>
              {canUpload && (
                <Button
                  color='primary'
                  size='small'
                  appearance='outlined'
                  className='mb-4 self-end text-primary-400'
                  startIcon={<UploadFileOutlined />}
                  onClick={() => setDialogOpen(true)}
                  data-testid='add-file-button'
                >
                  Upload Files
                </Button>
              )}

              <PurchaseOrderFileTable
                data={fileList}
                isLoading={fileFetching}
                onRemove={isReadonly ? async () => {} : handleFileRemove}
                isReadonly={isReadonly}
              />
            </Box>
          ) : (
            <InformationMissingAlert
              title='Upload Files'
              action={
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<UploadFileOutlined />}
                  onClick={() => setDialogOpen(true)}
                  disabled={!canUpload}
                  data-testid='add-file-button'
                >
                  Upload Files
                </Button>
              }
              description='PDF, PNG, JPG or DOC (max. 10MB)'
              data-testid='purchase-order-file-alert'
            />
          )}
        </Collapse>
      </Paper>
    </>
  )
}
